import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config/config';
import Skeleton from './common/Skeleton';
import EmptyState from './common/EmptyState';

const SEVERITY_COLORS = { CRITICAL: '#d32f2f', HIGH: '#f57c00', MEDIUM: '#fbc02d', LOW: '#1976d2', INFO: '#90a4ae' };
const SEVERITY_ORDER = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3, INFO: 4 };

function SeverityBadge({ severity }) {
  const s = (severity || 'INFO').toUpperCase();
  const cls = { CRITICAL: 'critical', HIGH: 'high', MEDIUM: 'medium', LOW: 'low', INFO: 'info' }[s] || 'none';
  return <span className={`ce-sev ce-sev--${cls}`}>{s}</span>;
}

function RepoCodeAnalysis({ repoName }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState(null);
  const navigate = useNavigate();

  const loadData = () => {
    setLoading(true);
    setError(null);
    axios.get(`${config.BASE_URL}/vigil/repo-findings/${encodeURIComponent(repoName)}`)
      .then(res => setData(res.data))
      .catch(err => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, [repoName]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSarifImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportMsg(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      let parsed;
      try { parsed = JSON.parse(ev.target.result); }
      catch { setImportMsg({ error: 'Invalid JSON file.' }); setImporting(false); return; }
      axios.post(`${config.BASE_URL}/vigil/repo-findings/${encodeURIComponent(repoName)}/sarif`, parsed)
        .then(res => {
          setImportMsg({ success: `Imported ${res.data.totalFindings} SAST findings.` });
          loadData();
        })
        .catch(err => setImportMsg({ error: err.response?.data?.error || err.message }))
        .finally(() => setImporting(false));
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const findings = useMemo(() => {
    if (!data?.findings) return [];
    let items = [...data.findings];
    if (filter !== 'ALL') {
      items = items.filter(f => (f.severity || '').toUpperCase() === filter);
    }
    if (search) {
      const q = search.toLowerCase();
      items = items.filter(f =>
        (f.ruleId || '').toLowerCase().includes(q) ||
        (f.message || '').toLowerCase().includes(q) ||
        (f.file || '').toLowerCase().includes(q) ||
        (f.category || '').toLowerCase().includes(q)
      );
    }
    items.sort((a, b) => (SEVERITY_ORDER[(a.severity || '').toUpperCase()] ?? 99) - (SEVERITY_ORDER[(b.severity || '').toUpperCase()] ?? 99));
    return items;
  }, [data, filter, search]);

  if (loading) return <Skeleton lines={8} />;
  if (error) return <EmptyState icon="⚠️" title="Error" description={error} />;
  if (!data?.latestScan) {
    return (
      <div>
        <EmptyState
          icon="🔍"
          title={t('codeAnalysis.noScans')}
          description={t('codeAnalysis.noScansDesc')}
          action={{ label: t('codeAnalysis.runScan'), onClick: () => navigate('/vigil/scan') }}
        />
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <label className="ce-btn ce-btn--secondary" style={{ cursor: importing ? 'wait' : 'pointer', opacity: importing ? 0.6 : 1 }}>
            {importing ? '⏳ Importing…' : '📥 Import SARIF from vigil scan'}
            <input type="file" accept=".json,.sarif" onChange={handleSarifImport} style={{ display: 'none' }} />
          </label>
          {importMsg && (
            <div style={{ marginTop: 8, color: importMsg.error ? '#c62828' : '#2e7d32', fontSize: '0.82rem' }}>
              {importMsg.error || importMsg.success}
            </div>
          )}
        </div>
      </div>
    );
  }

  const scan = data.latestScan;
  const filters = ['ALL', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'];
  const counts = { ALL: data.findings?.length || 0, CRITICAL: scan.critical, HIGH: scan.high, MEDIUM: scan.medium, LOW: scan.low, INFO: scan.info };

  return (
    <div>
      {/* Summary cards */}
      <div className="ce-kpi-row" style={{ marginBottom: '1rem' }}>
        {[
          { label: t('codeAnalysis.totalFindings'), value: scan.totalFindings, color: '#6366f1' },
          { label: t('codeAnalysis.critical'), value: scan.critical, color: '#dc2626' },
          { label: t('codeAnalysis.high'), value: scan.high, color: '#ea580c' },
          { label: t('codeAnalysis.medium'), value: scan.medium, color: '#ca8a04' },
          { label: t('codeAnalysis.low'), value: scan.low, color: '#16a34a' },
        ].map(c => (
          <div key={c.label} className="ce-kpi" style={{ '--kpi-color': c.color }}>
            <div className="ce-kpi-value">{c.value}</div>
            <div className="ce-kpi-label">{c.label}</div>
          </div>
        ))}
      </div>

      {/* Scan metadata + SARIF import */}
      <div className="ce-filter-bar" style={{ marginBottom: '0.75rem' }}>
        <span className="ce-tag">📋 {scan.totalRules} {t('codeAnalysis.rulesApplied')}</span>
        <span className="ce-tag">⏱️ {scan.duration}</span>
        <span className="ce-tag">🏷️ {scan.rulePacks || 'all'}</span>
        <Link to="/vigil/scan" style={{ marginLeft: 'auto', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--accent)' }}>
          🔍 {t('codeAnalysis.openVigilScanner')}
        </Link>
        <label className="ce-btn ce-btn--secondary ce-btn--sm" style={{ cursor: importing ? 'wait' : 'pointer', opacity: importing ? 0.6 : 1 }}>
          {importing ? '⏳ Importing…' : '📥 Import SARIF'}
          <input type="file" accept=".json,.sarif" onChange={handleSarifImport} style={{ display: 'none' }} />
        </label>
      </div>
      {importMsg && (
        <div style={{
          marginBottom: 12, padding: '8px 12px', borderRadius: 6, fontSize: '0.82rem',
          background: importMsg.error ? '#fff3f3' : '#f0fff4',
          border: `1px solid ${importMsg.error ? '#f5c6cb' : '#b2dfdb'}`,
          color: importMsg.error ? '#c62828' : '#2e7d32',
        }}>
          {importMsg.error || importMsg.success}
        </div>
      )}

      {/* Filter bar */}
      <div className="ce-filter-bar" style={{ marginBottom: '0.75rem' }}>
        {filters.map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`ce-btn ce-btn--sm ${filter === f ? 'ce-btn--primary' : 'ce-btn--secondary'}`}
          >
            {f} ({counts[f] || 0})
          </button>
        ))}
        <input
          type="text"
          placeholder={t('codeAnalysis.searchPlaceholder')}
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="ce-search-input"
          style={{ marginLeft: 'auto' }}
        />
      </div>

      {/* Findings table */}
      <div className="ce-table-wrap">
        <table className="ce-table">
          <thead>
            <tr>
              <th>{t('codeAnalysis.severity')}</th>
              <th>{t('codeAnalysis.rule')}</th>
              <th>{t('codeAnalysis.category')}</th>
              <th>{t('codeAnalysis.file')}</th>
              <th>{t('codeAnalysis.line')}</th>
              <th>{t('codeAnalysis.message')}</th>
            </tr>
          </thead>
          <tbody>
            {findings.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: 'var(--bs-secondary-color)' }}>
                {filter === 'ALL' && !search ? t('codeAnalysis.noFindings') : t('codeAnalysis.noMatch')}
              </td></tr>
            ) : findings.map((f, i) => (
              <tr key={i}>
                <td><SeverityBadge severity={f.severity} /></td>
                <td className="ce-mono">{f.ruleId || '-'}</td>
                <td>{f.category || '-'}</td>
                <td className="ce-mono" style={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.file}>{f.file || '-'}</td>
                <td style={{ textAlign: 'center' }}>{f.line || '-'}</td>
                <td style={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.message}>{f.message || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Scan history */}
      {data.history?.length > 1 && (
        <div style={{ marginTop: '1.5rem' }}>
          <div className="ce-section-header">
            <h6 className="ce-section-title">{t('codeAnalysis.scanHistory')}</h6>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', overflowX: 'auto', paddingBottom: '0.25rem' }}>
            {data.history.map(h => (
              <div key={h.id} className="ce-card" style={{
                padding: '0.625rem 0.875rem', minWidth: 120, textAlign: 'center',
                background: h.id === scan.id ? 'color-mix(in srgb, var(--accent) 8%, var(--bs-body-bg))' : undefined,
              }}>
                <div style={{ fontWeight: 700, fontSize: '0.875rem' }}>{h.totalFindings} findings</div>
                <div style={{ fontSize: '0.7rem', color: 'var(--bs-secondary-color)' }}>{h.status}</div>
                <div style={{ fontSize: '0.7rem', color: 'var(--bs-secondary-color)' }}>{h.duration}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default RepoCodeAnalysis;
